import React, { useState } from "react";
import { format } from "date-fns";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css"; // Import calendar styles

// Simple Card component
const Card = ({ children, className }) => (
  <div className={`bg-white p-4 rounded-lg shadow-md ${className}`}>{children}</div>
);

const CardContent = ({ children, className, onClick }) => (
  <div
    className={`p-4 rounded-lg border bg-gray-50 hover:shadow-lg cursor-pointer ${className}`}
    onClick={onClick}
  >
    {children}
  </div>
);

// Simple Button component
const Button = ({ children, className, onClick }) => (
  <button
    className={`bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg ${className}`}
    onClick={onClick}
  >
    {children}
  </button>
);

// Input component
const Input = ({ placeholder, value, onChange, readOnly = false, className, onClick }) => (
  <input
    className={`w-full border rounded-lg p-2 ${className}`}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    readOnly={readOnly}
    onClick={onClick}
  />
);

// Textarea component
const Textarea = ({ placeholder, value, onChange, className }) => (
  <textarea
    className={`w-full border rounded-lg p-2 ${className}`}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
  />
);

export default function KanbanBoard() {
  const [tasks, setTasks] = useState({
    backlog: [
      { title: "Athlete Accident", priority: "Medium", team: "Sport Law Team", status: "Backlog", date: "2025-04-12", description: "Details about the athlete accident." },
      { title: "New Home Case", priority: "Low", team: "Civil Rights Team", status: "Backlog", date: "2025-04-12", description: "Details about the new home case." },
      { title: "Fraud Case", priority: "High", team: "Civil Rights Team", status: "Backlog", date: "2025-04-12", description: "Details about the fraud case." },
    ],
    doingNext: [
      { title: "Car Accident", priority: "Low", team: "Civil Rights Team", status: "Doing Next", date: "2025-04-12", description: "Details about the car accident." },
    ],
    ongoing: [
      { title: "Box Match Case", priority: "Low", team: "Sports Law Team", status: "Ongoing", date: "2025-04-12", description: "Details about the box match case." },
    ],
    done: [],
  });

  const [selectedTask, setSelectedTask] = useState(null);
  const [newTask, setNewTask] = useState({
    title: "",
    priority: "Low",
    team: "",
    status: "Backlog",
    description: "",
    dueDate: "",
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setNewTask(task);
    setIsDialogOpen(true);
  };

  const updateTask = () => {
    setTasks((prev) => {
      const updatedTasks = { ...prev };

      // Remove the task from all lists
      for (const list in updatedTasks) {
        updatedTasks[list] = updatedTasks[list].filter((task) => task.title !== selectedTask.title);
      }

      // Normalize the status to match the state keys
      const targetList = newTask.status.toLowerCase().replace(/\s+/g, "");
      const normalizedTargetList = Object.keys(updatedTasks).find(
        (key) => key.toLowerCase() === targetList
      );

      if (normalizedTargetList) {
        updatedTasks[normalizedTargetList].push({ ...newTask });
        console.log(`Task successfully added to: ${normalizedTargetList}`);
      } else {
        console.error(`List '${normalizedTargetList}' not found in tasks state.`);
      }

      console.log("Updated tasks state:", updatedTasks);

      return updatedTasks;
    });

    setSelectedTask(null);
    setNewTask({ title: "", priority: "Low", team: "", status: "Backlog", description: "", dueDate: "" });
    setIsDialogOpen(false);
  };

  const addTask = () => {
    if (!newTask.title.trim()) {
      alert("Task title is required");
      return;
    }

    setTasks((prev) => {
      const targetList = newTask.status.toLowerCase().replace(/\s+/g, "");
      const normalizedTargetList = Object.keys(prev).find(
        (key) => key.toLowerCase() === targetList
      );

      if (normalizedTargetList) {
        const updatedState = {
          ...prev,
          [normalizedTargetList]: [...prev[normalizedTargetList], { ...newTask }],
        };
        console.log("New task added successfully. Updated state:", updatedState);
        return updatedState;
      } else {
        console.error(`List '${normalizedTargetList}' not found in tasks state.`);
        return prev;
      }
    });

    setNewTask({ title: "", priority: "Low", team: "", status: "Backlog", description: "", dueDate: "" });
    setIsDialogOpen(false);
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard 1</h1>
        <Button
          onClick={() => {
            setSelectedTask(null);
            setNewTask({ title: "", priority: "Low", team: "", status: "Backlog", description: "", dueDate: "" });
            setIsDialogOpen(true);
          }}
        >
          Add New Task
        </Button>
      </div>

      {isDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg p-6 shadow-lg max-w-md w-full">
            <h2 className="text-lg font-bold mb-4">{selectedTask ? "Update Task" : "New Task"}</h2>
            <Input
              placeholder="Task Title"
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
            />
            <select
              className="w-full border rounded-lg p-2 mt-2"
              value={newTask.priority}
              onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
            >
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
            <Input
              placeholder="Assigned Team"
              value={newTask.team}
              onChange={(e) => setNewTask({ ...newTask, team: e.target.value })}
              className="mt-2"
            />
            <select
              className="w-full border rounded-lg p-2 mt-2"
              value={newTask.status}
              onChange={(e) => setNewTask({ ...newTask, status: e.target.value })}
            >
              <option value="Backlog">Backlog</option>
              <option value="Doing Next">Doing Next</option>
              <option value="Ongoing">Ongoing</option>
              <option value="Done">Done</option>
            </select>
            <Textarea
              placeholder="Description"
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              className="mt-2"
            />
            <div className="relative">
              <Input
                placeholder="Due Date"
                value={newTask.dueDate ? format(new Date(newTask.dueDate), "yyyy-MM-dd") : ""}
                readOnly
                onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                className="mt-2 cursor-pointer"
              />
              {isCalendarOpen && (
                <div className="absolute z-10 bg-white shadow-lg rounded-lg p-2">
                  <Calendar
                    onChange={(date) => {
                      setNewTask({ ...newTask, dueDate: date });
                      setIsCalendarOpen(false);
                    }}
                    value={newTask.dueDate ? new Date(newTask.dueDate) : new Date()}
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end mt-4">
              <Button className="mr-2" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={selectedTask ? updateTask : addTask}>
                {selectedTask ? "Update Task" : "Add Task"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-4 gap-4 mt-6">
        {Object.entries(tasks).map(([listName, tasksList]) => (
          <Card key={listName}>
            <h2 className="text-lg font-semibold mb-2 capitalize">{listName}</h2>
            {tasksList.map((task, idx) => (
              <CardContent key={idx} onClick={() => handleTaskClick(task)}>
                <h3 className="font-medium">{task.title}</h3>
                <p>{task.description}</p>
                <p className="text-sm text-gray-500">Priority: {task.priority}</p>
                <p className="text-sm text-gray-500">Due Date: {task.date}</p>
              </CardContent>
            ))}
          </Card>
        ))}
      </div>
    </div>
  );
}
